﻿import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faSpinner } from '@fortawesome/free-solid-svg-icons';
import clsx from "clsx";

import { Order } from "../../types/Order";

export type TableHeadCell = {
  name: string,
  sortable?: boolean,
  tailwindClasses?: string
  tabLockKey?: string
}

interface TableHeadProps {
  headCells: TableHeadCell[];
  showDetailsColumnHeadCell: boolean;
  isLoading: boolean;
  isNestedTable?: boolean;
  isEmpty: boolean;
  sortDirection: Order;
  sortField?: string;
  sortClickHandler: (property: string, order: Order) => void;
  overrideHeaderCells?: TableHeadCell[];
  enableTabLocking?: boolean;
  lockedTabKey?: string | null;
  setLockedTabKey?: (key: string | null) => void;
  /** True when the scroll container has been scrolled away from the top.
   *  Used to fade in a small drop-shadow under the sticky header. */
  scrolledPast?: boolean;
}

const DataTableHead = ({
  headCells,
  showDetailsColumnHeadCell,
  isLoading,
  isNestedTable,
  isEmpty,
  sortDirection,
  sortField,
  sortClickHandler,
  overrideHeaderCells,
  enableTabLocking,
  lockedTabKey,
  setLockedTabKey,
  scrolledPast,
}: TableHeadProps) => {
  const [sortOrder, setSortOrder] = useState<Order>(sortDirection);
  const [sortBy, setSortBy] = useState<string | undefined>(sortField);

  const onSortClick = (property: string) => {
    setSortBy(property);
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    sortClickHandler(property, sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const renderLockButton = (headCell: TableHeadCell) => {
    if (!enableTabLocking || !headCell.tabLockKey || !setLockedTabKey) return null;

    const isLocked = lockedTabKey === headCell.tabLockKey;

    return (
      <button
        type="button"
        className={`rounded transition ${isLocked
          ? "text-theme-warning hover:text-theme-warning-hover"
          : "text-theme-text hover:text-theme-text-hover"
          }`}
        onClick={(e) => {
          e.stopPropagation();
          setLockedTabKey(isLocked ? null : headCell.tabLockKey!);
        }}
        aria-label={isLocked ? `Unlock ${headCell.name} tabbing` : `Lock ${headCell.name} tabbing`}
        title={isLocked ? `Unlock ${headCell.name} tabbing` : `Lock ${headCell.name} tabbing`}
      >
        <FontAwesomeIcon icon={faLock} />
      </button>
    );
  };

  const renderTableHeaderCell = (headCell: TableHeadCell, headCellIndex: number) => {
    const isLocked = !!headCell.tabLockKey && lockedTabKey === headCell.tabLockKey;

    if (headCell.sortable) {
      return (
        <li
          key={headCellIndex}
          className={clsx(
            headCell.tailwindClasses,
            "flex items-center justify-center text-[0.9rem] w-full fade-up",
          )}
        >
          <button
            type="button"
            className={`cursor-pointer group w-full py-2 px-4 compact:px-2 compact:py-1 text-theme-table-heading-contrast hover:text-theme-text-muted transition flex items-center justify-center ${isLocked ? "ring-1 ring-inset ring-theme-info rounded-sm" : ""}`}
            onClick={() => onSortClick(headCell.name)}
          >
            <span className="flex gap-1 items-center">
              {headCell.name}
              {sortBy?.toLowerCase() === headCell.name.toLowerCase() ? (
                <span>{sortOrder === 'desc' ? '↑' : '↓'}</span>
              ) : (
                <span className="opacity-30 group-hover:opacity-70 transition-opacity">↕</span>
              )}
            </span>
            {renderLockButton(headCell)}
          </button>
        </li>
      );
    }

    return (
      <li
        key={headCellIndex}
        className={clsx(
          headCell.tailwindClasses,
          "flex items-center justify-center text-[0.9rem] text-theme-table-heading-contrast w-full fade-up",
        )}
      >
        <div className="w-full flex items-center gap-1 justify-center p-2 compact:p-0">
          <span>{headCell.name}</span>
          {renderLockButton(headCell)}
        </div>
      </li>
    );
  };

  return (
    <div className={`${isNestedTable ? '' : `sticky top-0 z-10 bg-theme-surface-2 data-table-sticky-head ${scrolledPast ? 'data-table-sticky-head--scrolled' : ''}`}`}>
      <div className="line border-b border-theme-table-border">
        <ul className="heading bg-theme-table-heading transition">
          {isLoading ?
            <li className="min-h-[2.5rem] text-theme-table-heading-contrast flex items-center justify-center text-[0.9rem] w-full fade-up"><FontAwesomeIcon icon={faSpinner} spin /></li> :
            (
              <>
                {overrideHeaderCells ? overrideHeaderCells.map((headCell, index) => renderTableHeaderCell(headCell, index)) :
                  <>
                    {
                      isEmpty ?
                        (
                          <li className="block text-theme-table-heading-contrast text-center min-h-[2.5rem] flex items-center justify-center text-[0.9rem] w-full fade-up">No data</li>
                        )
                        :
                        (<>
                          {headCells.map((headCell, index) => renderTableHeaderCell(headCell, index))}
                          {showDetailsColumnHeadCell && <li key='details' className={'basis-[5%] flex items-center justify-center text-[0.9rem] text-theme-table-heading-contrast w-full'}>Details</li>}
                        </>)
                    }
                  </>
                }
              </>
            )
          }
        </ul>
      </div>
    </div>
  )
}

export default DataTableHead;
